/**
 * Página principal de recursos.
 *
 * Este componente renderiza a interface de gerenciamento de recursos,
 * permitindo visualizar, criar, editar e excluir recursos e categorias.
 * O layout é dividido em três seções principais: categorias, lista de recursos
 * e visualização de conteúdo.
 *
 * @module Resources
 */

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import draftToHtml from 'draftjs-to-html';
import parse from 'html-react-parser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHref, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import ContainerPage from '../../components/ContainerPage/ContainerPage';
import Modal from '../../components/Resources/Modal/Modal';
import { useCategories } from '../../hooks/useCategories';
import { useResourceCategories } from '../../hooks/useResourceCategories';
import { useResources } from '../../hooks/useResources';
import '../../index.css';
import {
  AlertContainer,
  ContentModal,
  Header,
  HeaderCategory,
  Main,
  SectionCategory,
  SectionResource,
  SectionText,
} from './Resources.Styles';

/**
 * Componente funcional que renderiza a página de recursos.
 *
 * @returns {JSX.Element} Componente de página de recursos
 */
const Resources = () => {
  // Estados e funções do hook personalizado useResources
  const {
    resources,
    selectedCategory,
    titleCategory,
    setTitleCategory,
    cardSelected,
    setCardSelected,
    isLoading,
    filterResourcesByCategory,
    deleteResource,
    clearSelection,
    snackbarMessage: resourceSnackbarMessage,
    snackbarSeverity: resourceSnackbarSeverity,
    snackbarOpen: resourceSnackbarOpen,
    setSnackbarOpen: setResourceSnackbarOpen,
  } = useResources();

  // Estados e funções do hook personalizado useCategories
  const {
    categories,
    numResources,
    createCategory,
    deleteCategory,
    editCategory,
    snackbarMessage: categorySnackbarMessage,
    snackbarSeverity: categorySnackbarSeverity,
    snackbarOpen: categorySnackbarOpen,
    setSnackbarOpen: setCategorySnackbarOpen,
  } = useCategories();

  const { data, loading, error } = useResourceCategories();

  // Estados locais para gerenciar modais e formulário de nova categoria
  const [newCategory, setNewCategory] = useState('');
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isDeleteCategoryOpen, setIsDeleteCategoryOpen] = useState(false);
  const [isDeleteResourceOpen, setIsDeleteResourceOpen] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [resourceError, setResourceError] = useState(false);

  // Hooks para navegação e tradução
  const href = useHref();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentPage = href.replace('/', '');

  const handleDeleteResource = () => {
    deleteResource(cardSelected.id);
    closeDeleteResource();
  };

  const handleDeleteCategory = async () => {
    await deleteCategory(selectedCategory);
    clearSelection();
    closeDeleteCategory();
  };

  /**
   * Seleciona um recurso para visualização detalhada.
   *
   * @function resourceClick
   * @param {string|number} id - ID do recurso a ser selecionado
   */
  const resourceClick = (id) => {
    const foundResource = resources.find((res) => res.id === id);
    setCardSelected(foundResource);
  };

  /**
   * Navega para a página de edição de um recurso.
   *
   * @function resourceEdit
   * @param {string|number} id - ID do recurso a ser editado
   */
  const resourceEdit = (id) => {
    navigate('/editResources', {
      state: { id },
    });
  };

  const cleanJsonString = (str) => str.replace(/\r?\n/g, '\\n');

  /**
   * Converte o conteúdo do Draft.js para texto simples.
   *
   * @function convertDraftToText
   * @param {string} draftJson - JSON do Draft.js a ser convertido
   * @returns {string} Texto extraído do conteúdo do Draft.js
   */

  const convertDraftToText = (draftJson) => {
    try {
      const cleaned = cleanJsonString(draftJson);
      const parsed = JSON.parse(cleaned);
      return parsed.blocks.map((block) => block.text).join('\n');
    } catch (err) {
      console.error('Erro:', err);
      return draftJson;
    }
  };

  /**
   * Converte o conteúdo do Draft.js para elementos React.
   *
   * @function convertDraftToReact
   * @param {string} draftJson - JSON do Draft.js a ser convertido
   * @returns {JSX.Element} Elementos React gerados a partir do conteúdo do Draft.js
   */
  const convertDraftToReact = (draftJson) => {
    try {
      const cleaned = cleanJsonString(draftJson);
      const raw = JSON.parse(cleaned);
      const html = draftToHtml(raw);
      return parse(html);
    } catch (err) {
      console.error('Erro:', err);
      return draftJson;
    }
  };

  // Funções para controlar a abertura e fechamento dos modais
  const openCreateCategory = () => {
    setCategoryError(false);
    setIsCreateCategoryOpen(true);
  };
  const openEditCategory = () => {
    setCategoryError(false);
    setIsEditCategoryOpen(true);
  };

  const closeCreateCategory = () => setIsCreateCategoryOpen(false);
  const closeEditCategory = () => setIsEditCategoryOpen(false);
  const openDeleteCategory = () => setIsDeleteCategoryOpen(true);
  const closeDeleteCategory = () => setIsDeleteCategoryOpen(false);
  const openDeleteResource = () => setIsDeleteResourceOpen(true);
  const closeDeleteResource = () => setIsDeleteResourceOpen(false);

  /**
   * Trunca uma descrição para exibição em cards.
   *
   * @function truncateDescription
   * @param {string} description - Descrição a ser truncada
   * @param {number} [maxLength=25] - Comprimento máximo antes de truncar
   * @returns {string} Descrição truncada com reticências se necessário
   */
  const truncateDescription = (description, maxLength = 25) =>
    description.length > maxLength
      ? description.substring(0, maxLength) + '...'
      : description;

  const selected = data?.find((c) => c.id === selectedCategory);

  return (
    <ContainerPage title={currentPage}>
      <Main>
        {/* Seção de categorias (primeira coluna) */}
        <SectionCategory>
          {!isLoading && (
            <div className="buttons">
              <Link to={'/newResources'}>
                <Button text={t('New Resource')} variant={'lg'} />
              </Link>
              <Button
                onClick={openCreateCategory}
                text={t('New Category')}
                variant={'lg'}
              />

              {/* Modal para criar nova categoria */}
              <Modal
                show={isCreateCategoryOpen}
                handleClose={closeCreateCategory}
              >
                <Header>
                  <span className="title-modal">{t('Create Category')}</span>
                  <button onClick={closeCreateCategory}>
                    <CloseIcon htmlColor="black" fontSize="medium" />
                  </button>
                </Header>
                <ContentModal>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!newCategory.trim()) {
                        setCategoryError(true);
                        return;
                      }
                      createCategory(newCategory.trim());
                      setNewCategory('');
                      setCategoryError(false);
                      closeCreateCategory();
                    }}
                  >
                    {categoryError && (
                      <AlertContainer>
                        <Alert severity="warning">
                          {t('Please enter a category name')}
                        </Alert>
                      </AlertContainer>
                    )}
                    <input
                      type="text"
                      name="newCategory"
                      id="newCategory"
                      value={newCategory}
                      onChange={(e) => {
                        setNewCategory(e.target.value);
                        if (e.target.value.trim()) setCategoryError(false);
                      }}
                      placeholder={t('Category Name')}
                    />
                    <div>
                      <Button text={t('Save')} variant={''} />
                    </div>
                  </form>
                </ContentModal>
              </Modal>
            </div>
          )}

          {/* Lista de categorias */}
          {!isLoading && (
            <div className="categories">
              {categories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() =>
                    filterResourcesByCategory(category?.id, category?.name)
                  }
                  className={
                    selectedCategory === category?.id ? 'selected' : ''
                  }
                >
                  <span className="category-name">{category?.name}</span>
                  <span>
                    {numResources?.find(
                      (num) => num.category_id === category.id
                    )?.resources || 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </SectionCategory>

        {/* Seção de recursos (segunda coluna) */}
        <SectionResource>
          {/* Cabeçalho da seção de recursos */}
          {!isLoading && (
            <HeaderCategory>
              <span className="title-header">{titleCategory}</span>
              {titleCategory && (
                <div className="buttons-category">
                  <button onClick={openEditCategory}>
                    <EditIcon htmlColor="black" fontSize="small" />
                  </button>
                  <button onClick={openDeleteCategory}>
                    <DeleteIcon
                      htmlColor="rgba(0, 0, 0, 0.6)"
                      fontSize="small"
                    />
                  </button>
                </div>
              )}
            </HeaderCategory>
          )}

          {/* Modal para excluir categoria */}
          <Modal show={isDeleteCategoryOpen} handleClose={closeDeleteCategory}>
            <Header>
              <span className="title-modal">{t('Delete Category')}</span>
              <button onClick={closeDeleteCategory}>
                <CloseIcon htmlColor="black" fontSize="medium" />
              </button>
            </Header>
            <ContentModal>
              <div className="text-alert">
                <p>{t('alert-delete')}</p>
              </div>
              <div className="buttons-delete">
                <Button
                  onClick={closeDeleteCategory}
                  text={t('Cancel')}
                  variant={'lg'}
                />
                <Button
                  onClick={handleDeleteCategory}
                  text={t('Confirm')}
                  variant="lg"
                  disabled={selected ? selected.amountResources > 0 : false}
                />
              </div>
            </ContentModal>
          </Modal>

          {/* Modal para editar categoria */}
          <Modal show={isEditCategoryOpen} handleClose={closeEditCategory}>
            <Header>
              <span className="title-modal">{t('Edit Category')}</span>
              <button onClick={closeEditCategory}>
                <CloseIcon htmlColor="black" fontSize="medium" />
              </button>
            </Header>
            <ContentModal>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!titleCategory.trim()) {
                    setCategoryError(true);
                    return;
                  }
                  editCategory(selectedCategory, titleCategory.trim());
                  setCategoryError(false);
                  closeEditCategory();
                }}
              >
                {categoryError && (
                  <AlertContainer>
                    <Alert severity="warning">
                      {t('Please enter a category name')}
                    </Alert>
                  </AlertContainer>
                )}
                <input
                  type="text"
                  name="edit-category"
                  id="name-edit"
                  placeholder={t('Category Name')}
                  value={titleCategory}
                  onChange={(e) => {
                    setTitleCategory(e.target.value);
                    if (e.target.value.trim()) setCategoryError(false);
                  }}
                />
                <div>
                  <Button text={t('Save')} variant={''} />
                </div>
              </form>
            </ContentModal>
          </Modal>

          {/* Lista de recursos */}
          <div className="list-cards">
            {isLoading ? (
              <div className="loading">
                <CircularProgress size={18} />
              </div>
            ) : (
              resources.map((resource) => (
                <button
                  key={resource.id}
                  className={`card-category ${cardSelected?.id === resource.id ? 'cardSelected' : ''}`}
                  onClick={() => resourceClick(resource.id)}
                >
                  <div className="info-card">
                    <h1>{resource?.title}</h1>
                    <p>
                      {truncateDescription(
                        convertDraftToText(resource?.description)
                      )}
                    </p>
                  </div>
                  <div className="buttons-card">
                    <button onClick={() => resourceEdit(resource.id)}>
                      <EditIcon
                        htmlColor="rgba(0, 0, 0, 0.6)"
                        fontSize="small"
                      />
                    </button>
                    <button onClick={openDeleteResource}>
                      <DeleteIcon
                        htmlColor="rgba(0, 0, 0, 0.6)"
                        fontSize="small"
                      />
                    </button>
                  </div>
                </button>
              ))
            )}

            <Modal
              show={isDeleteResourceOpen}
              handleClose={closeDeleteResource}
            >
              <Header>
                <span className="title-modal">{t('Delete Resource')}</span>
                <button onClick={closeDeleteResource}>
                  <CloseIcon htmlColor="black" fontSize="medium" />
                </button>
              </Header>
              <ContentModal>
                <div className="text-alert">
                  <p>{t('Are you sure you want to delete this resource?')}</p>
                </div>

                {resourceError && (
                  <AlertContainer>
                    <Alert severity="error">
                      {t(
                        'There was an error deleting the resource. Please try again.'
                      )}
                    </Alert>
                  </AlertContainer>
                )}

                <div className="buttons-delete">
                  <Button
                    onClick={closeDeleteResource}
                    text={t('Cancel')}
                    variant={'lg'}
                  />
                  <Button
                    onClick={handleDeleteResource}
                    text={t('Confirm')}
                    variant={'lg'}
                  />
                </div>
              </ContentModal>
            </Modal>
          </div>
        </SectionResource>

        {/* Seção de visualização de conteúdo (terceira coluna) */}
        {cardSelected && (
          <SectionText>
            <div className="text-header">
              <span>{cardSelected?.title}</span>
              <Button text={t('Send')} variant={''} />
            </div>
            <div className="text-page">
              {convertDraftToReact(cardSelected?.description)}
            </div>
          </SectionText>
        )}
      </Main>

      {/* Snackbars para notificações */}
      {resourceSnackbarOpen && (
        <Snackbar
          open={resourceSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setResourceSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setResourceSnackbarOpen(false)}
            severity={resourceSnackbarSeverity}
            sx={{ width: '100%' }}
          >
            {resourceSnackbarMessage}
          </Alert>
        </Snackbar>
      )}

      {categorySnackbarOpen && (
        <Snackbar
          open={categorySnackbarOpen}
          autoHideDuration={4000}
          onClose={() => setCategorySnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCategorySnackbarOpen(false)}
            severity={categorySnackbarSeverity}
            sx={{ width: '100%' }}
          >
            {categorySnackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </ContainerPage>
  );
};

export default Resources;
